import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal } from '@/components/ui'
import { useAuth } from '@/contexts/AuthContext'
import { useModal } from '@/contexts/ModalContext'
import { apiClient } from '@/lib/api'
import { redirectToRoleHome } from '@/lib/authRedirects'
import ForgotPasswordModal from './ForgotPasswordModal'

interface LoginModalProps {
  onSuccess?: () => void
}

const scrollFieldIntoView = (el: HTMLElement) => {
  window.setTimeout(() => {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, 120)
}

const LoginModal: React.FC<LoginModalProps> = ({ onSuccess }) => {
  const { t } = useTranslation()
  const { showModal, hideModal } = useModal()
  const { refreshAuth } = useAuth()
  const [loginIdentifier, setLoginIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async () => {
    setError(null)
    if (!loginIdentifier.trim() || !password) {
      setError(t('auth.loginIdentifierAndPasswordRequired', 'Username/email and password are required'))
      return
    }

    setLoading(true)
    try {
      const response = await apiClient.login(loginIdentifier.trim(), password)

      if (response.success && response.data) {
        const user = response.data.user
        await refreshAuth()
        showModal(null)
        await new Promise((resolve) => setTimeout(resolve, 200))
        redirectToRoleHome(user)
        onSuccess?.()
      } else if (response.rateLimited || response.error?.toLowerCase().includes('too many')) {
        setError(response.error || 'Too many failed login attempts. Please wait before trying again.')
      } else if (response.error === 'Network error') {
        setError(t('auth.networkError', 'Cannot reach the API. Is npm run dev running?'))
      } else {
        setError(response.error || t('auth.loginError', 'Incorrect email or password'))
      }
    } catch {
      setError(t('auth.loginError', 'Login failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen onClose={hideModal} size="sm" bare>
      {/* Standard login card: ~24rem, centered content, even padding */}
      <div className="relative w-full bg-white rounded-2xl border border-neutral-200 shadow-xl">
        <button
          type="button"
          onClick={hideModal}
          className="absolute right-3 top-3 z-10 p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-6 pt-8 pb-6 sm:px-8 sm:pt-9 sm:pb-7">
          <div className="flex flex-col items-center text-center mb-6">
            <img src="/logo.webp" alt="" className="h-10 w-10 object-contain mb-3" />
            <h2 className="text-xl font-semibold text-neutral-900 tracking-tight">
              {t('auth.loginTitle', 'Welcome Back')}
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              {t('auth.loginSubtitle', 'Sign in to continue')}
            </p>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              handleLogin()
            }}
          >
            <Input
              fullWidth
              type="text"
              label={t('auth.loginIdentifier', 'Username or Email')}
              placeholder={t('auth.loginIdentifierPlaceholder', 'Enter username or email')}
              value={loginIdentifier}
              onChange={(e) => setLoginIdentifier(e.target.value)}
              autoFocus
              autoComplete="username"
              onFocus={(e) => scrollFieldIntoView(e.currentTarget)}
            />

            <div className="w-full space-y-1.5">
              <div className="relative w-full">
                <Input
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label={t('auth.password', 'Password')}
                  placeholder={t('auth.passwordPlaceholder', 'Enter password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-11"
                  autoComplete="current-password"
                  onFocus={(e) => scrollFieldIntoView(e.currentTarget)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 bottom-[9px] p-1.5 text-neutral-400 hover:text-neutral-600 rounded-md"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.05 8.05m1.829 1.829l4.242 4.242M12 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-1.563 3.029m-5.858-.908a3 3 0 01-4.243-4.243" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    showModal({ component: ForgotPasswordModal })
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {t('auth.forgotPassword', 'Forgot Password?')}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 text-center leading-snug" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" loading={loading} className="w-full" size="lg">
              {loading ? t('auth.loggingIn', 'Signing in...') : t('auth.login', 'Sign In')}
            </Button>
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default LoginModal
